import { useEffect, useRef, useState } from 'react';
import { useRoute } from 'wouter';
import { Card, CardBody, Spinner } from '@nextui-org/react';
import { getPublicSharedReport } from 'common/src/api/sharedReports';
import ReportDocument from '../../components/client-reports/ReportDocument';
import SharedReportFrame, {
  SharedReportCard,
} from '../../components/client-reports/SharedReportFrame';
import { useReportFormat } from '../../components/client-reports/useReportFormat';
import type { ClientReportSnapshot } from '../../utils/client-reports/types';

const VISITOR_KEY = 'shared-report-visitor';

// Identificador anónimo del navegador, para contar personas distintas.
const getVisitorId = () => {
  try {
    let visitorId = localStorage.getItem(VISITOR_KEY);
    if (!visitorId) {
      visitorId = crypto.randomUUID();
      localStorage.setItem(VISITOR_KEY, visitorId);
    }
    return visitorId;
  } catch {
    return null;
  }
};

const PublicClientReportPage = () => {
  const [, params] = useRoute('/r/:token');
  const token = params?.token;
  const { t } = useReportFormat();
  const [snapshot, setSnapshot] = useState<ClientReportSnapshot | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'unavailable'>(
    'loading'
  );
  // Evita registrar dos vistas cuando React monta el efecto dos veces.
  const requestedToken = useRef<string | null>(null);

  useEffect(() => {
    if (!token || requestedToken.current === token) return;
    requestedToken.current = token;

    getPublicSharedReport<ClientReportSnapshot>(token, getVisitorId())
      .then((report) => {
        if (report?.snapshot) {
          setSnapshot(report.snapshot);
          setStatus('ready');
          document.title = report.title;
        } else {
          setStatus('unavailable');
        }
      })
      .catch((error) => {
        console.error('Error loading shared report:', error);
        setStatus('unavailable');
      });
  }, [token]);

  return (
    <SharedReportFrame>
      {status === 'loading' ? (
        <div className='flex justify-center p-16'>
          <Spinner />
        </div>
      ) : status === 'unavailable' || !snapshot ? (
        <div className='flex min-h-[60vh] items-center justify-center'>
          <Card className='w-full max-w-md'>
            <CardBody className='gap-2 p-8 text-center'>
              <h2 className='text-lg font-semibold'>
                {t('Este link no está disponible')}
              </h2>
              <p className='text-default-500'>
                {t(
                  'Puede que lo hayan desactivado. Pide uno nuevo a quien te lo envió.'
                )}
              </p>
            </CardBody>
          </Card>
        </div>
      ) : (
        <SharedReportCard>
          <ReportDocument snapshot={snapshot} />
        </SharedReportCard>
      )}
    </SharedReportFrame>
  );
};

export default PublicClientReportPage;
